import { Field, Panel } from '@/components/admin/Field'
import { lumi } from '@/lib/lumi'
import { uploadImage } from '@/lib/upload'
import type { HostAccount } from '@/types/host'
import { ImagePlus, Trash2 } from 'lucide-react'
import { FormEvent, useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'

function nowIso() {
  return new Date().toISOString()
}

const EMPTY: Partial<HostAccount> = { fullName: '', username: '', password: '', photoUrl: '', active: true }

function HostsManager() {
  const [hosts, setHosts] = useState<HostAccount[]>([])
  const [form, setForm] = useState<Partial<HostAccount>>(EMPTY)
  const [editingId, setEditingId] = useState('')
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const h = await lumi.entities.hosts.list({ sort: { createdAt: -1 }, limit: 100 })
      setHosts((h.list || []) as HostAccount[])
    } catch (error) {
      console.error('Falha ao carregar locutores:', error)
      toast.error('Não foi possível carregar os locutores.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const onPhoto = async (file: File | undefined) => {
    if (!file) return
    setUploading(true)
    try {
      const url = await uploadImage(file)
      setForm((prev) => ({ ...prev, photoUrl: url }))
      toast.success('Foto enviada.')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Falha no envio da foto.')
    } finally {
      setUploading(false)
    }
  }

  const save = async (event: FormEvent) => {
    event.preventDefault()
    const fullName = (form.fullName || '').trim()
    const username = (form.username || '').trim()
    const password = form.password || ''
    if (!fullName || !username || (!editingId && !password)) {
      toast.error('Preencha nome, utilizador e senha.')
      return
    }
    if (username.toLowerCase() === 'admin') {
      toast.error('Esse utilizador está reservado ao administrador.')
      return
    }
    const duplicated = hosts.some(
      (item) => item._id !== editingId && (item.username || '').trim().toLowerCase() === username.toLowerCase(),
    )
    if (duplicated) {
      toast.error('Já existe um locutor com esse utilizador.')
      return
    }
    try {
      const payload: Record<string, unknown> = {
        fullName,
        username,
        photoUrl: form.photoUrl || '',
        active: form.active !== false,
        updatedAt: nowIso(),
      }
      if (password) payload.password = password
      if (editingId) {
        await lumi.entities.hosts.update(editingId, payload)
      } else {
        await lumi.entities.hosts.create({ ...payload, password, createdAt: nowIso() })
      }
      toast.success('Locutor guardado.')
      setForm(EMPTY)
      setEditingId('')
      await load()
    } catch (error) {
      console.error('Falha ao guardar locutor:', error)
      toast.error('Não foi possível guardar o locutor.')
    }
  }

  const remove = async (id?: string) => {
    if (!id || !window.confirm('Remover este locutor?')) return
    try {
      await lumi.entities.hosts.delete(id)
      toast.success('Locutor removido.')
      await load()
    } catch (error) {
      console.error('Falha ao remover locutor:', error)
      toast.error('Não foi possível remover o locutor.')
    }
  }

  return (
    <div className="space-y-5">
      <div className="grid gap-5 lg:grid-cols-2">
        <Panel title={editingId ? 'Editar locutor' : 'Cadastrar locutor'}>
          <form onSubmit={save} className="grid gap-3">
            <Field label="Nome do locutor" value={form.fullName || ''} onChange={(e) => setForm({ ...form, fullName: e.target.value })} required />
            <Field label="Utilizador (login)" value={form.username || ''} onChange={(e) => setForm({ ...form, username: e.target.value })} required autoComplete="off" />
            <Field
              label={editingId ? 'Senha (deixe em branco para manter)' : 'Senha'}
              type="password"
              value={form.password || ''}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required={!editingId}
              autoComplete="new-password"
            />
            <div>
              <span className="mb-1.5 block text-sm font-semibold text-slate-700">Foto do locutor</span>
              <div className="flex items-center gap-3">
                {form.photoUrl ? <img src={form.photoUrl} alt="" className="h-14 w-14 rounded-full object-cover" /> : null}
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold">
                  <ImagePlus size={16} /> {uploading ? 'A enviar...' : 'Enviar foto'}
                  <input type="file" accept="image/*" className="sr-only" onChange={(e) => void onPhoto(e.target.files?.[0])} />
                </label>
              </div>
            </div>
            <label className="inline-flex items-center gap-2 text-sm font-semibold">
              <input type="checkbox" checked={form.active !== false} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              Conta ativa (aparece nos recadinhos)
            </label>
            <div className="flex gap-2">
              <button className="flex-1 rounded-full bg-[#0b2ea8] py-3 font-bold text-white">
                {editingId ? 'Guardar alterações' : 'Cadastrar locutor'}
              </button>
              {editingId ? (
                <button type="button" onClick={() => { setForm(EMPTY); setEditingId('') }} className="rounded-full border border-slate-200 px-4 font-semibold">
                  Cancelar
                </button>
              ) : null}
            </div>
          </form>
        </Panel>

        <Panel title="Locutores cadastrados">
          {loading ? <p className="text-sm text-slate-500">A carregar...</p> : null}
          <div className="space-y-3">
            {hosts.map((item) => (
              <article key={item._id} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
                {item.photoUrl ? (
                  <img src={item.photoUrl} alt="" className="h-11 w-11 rounded-full object-cover" />
                ) : (
                  <span className="grid h-11 w-11 place-items-center rounded-full bg-slate-100 text-sm font-bold text-slate-500">
                    {(item.fullName || '?').charAt(0)}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-bold">{item.fullName}</p>
                  <p className="text-sm text-slate-500">@{item.username} · {item.active === false ? 'Inativo' : 'Ativo'}</p>
                </div>
                <button type="button" className="text-sm font-bold text-[#0b2ea8]" onClick={() => { setForm({ ...item, password: '' }); setEditingId(item._id || '') }}>
                  Editar
                </button>
                <button type="button" onClick={() => void remove(item._id)} className="text-red-500" aria-label="Remover locutor">
                  <Trash2 size={16} />
                </button>
              </article>
            ))}
            {!loading && !hosts.length ? <p className="text-sm text-slate-500">Ainda não há locutores.</p> : null}
          </div>
        </Panel>
      </div>


    </div>
  )
}

export default HostsManager
